// frontend/src/utils/leaveTypeMapping.js

/**
 * Maps specific leave type names to generic privacy-protected labels
 * for display in department/company calendars (PDPA compliance).
 * 
 * @param {string} leaveTypeName - The specific leave type name from the backend
 * @returns {string} Generic leave type label
 */
export const mapToGenericLeaveType = (leaveTypeName) => {
  if (!leaveTypeName) return 'Out of Office';
  
  const name = leaveTypeName.toLowerCase();
  
  // External Activity: offsite work (meeting, seminar, workshop) — distinct from leave
  if (name.includes('external activity') ||
      name.includes('external')) {
    return 'External Activity';
  }

  // Out of Office: AL, Urgent, Home Leave, OIL, Marriage, Unpaid, CCL
  if (name.includes('annual leave') || 
      name.includes('urgent leave') ||
      name.includes('home leave') ||
      name.includes('off in lieu') || 
      name.includes('oil') ||
      name.includes('marriage') ||
      name.includes('unpaid leave') ||
      name.includes('childcare leave')) {
    return 'Out of Office';
  }
  
  // Unavailable: MC (Outpatient Sick), Hospitalisation
  if (name.includes('outpatient') || 
      name.includes('sick leave') ||
      name.includes('hospitalisation')) {
    return 'Unavailable';
  }
  
  // Extended Leave: ML, PL, Shared Parental
  if (name.includes('maternity') ||
      name.includes('paternity') ||
      name.includes('shared parental')) {
    return 'Extended Leave';
  }
  
  // Out of Office: Compassionate/Bereavement
  if (name.includes('compassionate') || name.includes('bereavement')) {
    return 'Out of Office';
  }
  
  // Work From Home: WFH, Flexi-Place
  if (name.includes('work from home') ||
      name.includes('wfh') ||
      name.includes('flexi')) {
    return 'Work From Home';
  }
  
  // On Course: Company-sanctioned training/course
  if (name === 'on course') {
    return 'On Course';
  }
  
  // National Service Leave
  if (name.includes('national service') || name === 'ns leave') {
    return 'NS Leave';
  }
  
  // Default fallback
  return 'Out of Office';
};

/**
 * Returns Tailwind CSS classes for generic leave type badges
 * 
 * UX-Optimized Color Scheme:
 * - Out of Office (Blue): Standard professional leave - calm, neutral
 * - Unavailable (Amber): Medical leave - caution tone, neutral
 * - Extended Leave (Rose): Family/parental leave - warmth, care, intuitive
 * - Work From Home (Green): Remote work - active, productive, distinct from blue
 * - External Activity (Teal): Offsite work - professional, active, distinct from WFH green
 * 
 * @param {string} genericType - The generic leave type label
 * @returns {string} Tailwind CSS classes for badge styling
 */
export const getGenericLeaveTypeColor = (genericType) => {
  const colors = {
    'Out of Office': 'bg-blue-100 text-blue-800 border-blue-300',
    'Unavailable': 'bg-amber-100 text-amber-800 border-amber-300',
    'Extended Leave': 'bg-rose-100 text-rose-800 border-rose-300',
    'Work From Home': 'bg-green-100 text-green-800 border-green-300',
    'External Activity': 'bg-teal-100 text-teal-800 border-teal-300',
    'On Course': 'bg-violet-100 text-violet-800 border-violet-300',
    'NS Leave': 'bg-lime-100 text-lime-800 border-lime-300',
  };
  return colors[genericType] || colors['Out of Office'];
};

